import type { User } from '~/types';
import type { LocaleCode } from '~/constants/i18n';
import { PREFERRED_CURRENCIES, type PreferredCurrency } from '~/constants/currency';

export type CurrencyCode = PreferredCurrency;
export type MeasureUnit = 'm2' | 'ft2';

const CURRENCY_OPTIONS: CurrencyCode[] = [...PREFERRED_CURRENCIES];
const MEASURE_OPTIONS: MeasureUnit[] = ['m2', 'ft2'];

export const usePreferencesStore = defineStore(
  'preferences',
  () => {
    const authStore = useAuthStore();
    const currency = ref<CurrencyCode>('AED');
    const measureUnit = ref<MeasureUnit>('m2');

    watch(
      () => authStore.user,
      (user) => {
        if (!user) return;
        if (user.preferredCurrency && CURRENCY_OPTIONS.includes(user.preferredCurrency as CurrencyCode)) {
          currency.value = user.preferredCurrency as CurrencyCode;
        }
        if (user.preferredMeasureUnit && MEASURE_OPTIONS.includes(user.preferredMeasureUnit as MeasureUnit)) {
          measureUnit.value = user.preferredMeasureUnit as MeasureUnit;
        }
      },
      { immediate: true },
    );

    type PreferencesPayload = {
      preferredCurrency?: CurrencyCode;
      preferredMeasureUnit?: MeasureUnit;
      languageIso?: LocaleCode;
    };

    // Rejects on failure — callers decide whether a failed save is worth surfacing.
    async function patchPreferences(payload: PreferencesPayload) {
      const user = authStore.user;
      const token = authStore.token;
      if (!user?.id || !token) return;
      const updated: User = await $fetch(`/api/user/${user.id}/preferences`, {
        method: 'PATCH',
        body: payload,
        headers: { Authorization: `Bearer ${token}` },
      });
      authStore.updateUser(updated);
    }

    async function persistPreferences(payload: PreferencesPayload) {
      try {
        await patchPreferences(payload);
      } catch {
        // keep local state on failure
      }
    }

    function setCurrency(code: CurrencyCode) {
      currency.value = code;
      persistPreferences({ preferredCurrency: code });
    }

    function setMeasureUnit(unit: MeasureUnit) {
      measureUnit.value = unit;
      persistPreferences({ preferredMeasureUnit: unit });
    }

    // Unlike currency/unit, a failed language save is not swallowed: `languageIso` outranks
    // the cookie, so the caller must surface the failure (see useLocaleSwitch.switchLocale).
    function persistLanguage(code: LocaleCode) {
      return patchPreferences({ languageIso: code });
    }

    return {
      currency,
      measureUnit,
      setCurrency,
      setMeasureUnit,
      persistLanguage,
      CURRENCY_OPTIONS,
      MEASURE_OPTIONS,
    };
  },
  { persist: true },
);
